// "<Student> – <Category> Progress" popup on the admin Progress Report: one tab per course of the category,
// the student's totals, and each assigned story with its content types (PDF, audio, video, game, quiz,
// practice), coins and status; "View Details" lists the story's items. Data comes from
// GET /api/v2/lms/admin/courses/progress-report/students/:id.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Eye,
  FileText,
  Gamepad2,
  Headphones,
  HelpCircle,
  Mic,
  PlayCircle,
  RefreshCw,
  X,
} from 'lucide-react';
import { api } from '../../api';
import LoadingState from '../../components/common/LoadingState';
import { fmtDate } from '../../components/adminCourses/exportCategoryProgress';

export const STATUS = {
  completed: { label: 'Completed', tone: 'green' },
  in_progress: { label: 'In Progress', tone: 'blue' },
  not_started: { label: 'Not Started', tone: 'red' },
};
export const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

// Page numbers with gaps, e.g. 1 … 4 5 6 … 10.
export function pageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [...new Set([1, total, current - 1, current, current + 1])].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  return pages.flatMap((p, i) => (i && p - pages[i - 1] > 1 ? ['…', p] : [p]));
}

const STORIES_PER_PAGE = 10;
// Story content columns; image and text items count towards progress but have no column.
const TYPE_COLUMNS = [
  { key: 'pdf', label: 'PDF', types: ['pdf'], Icon: FileText, color: '#dc2626' },
  { key: 'audio', label: 'Audio', types: ['audio'], Icon: Headphones, color: '#0d9488' },
  { key: 'video', label: 'Video', types: ['video'], Icon: PlayCircle, color: '#2563eb' },
  { key: 'game', label: 'Game / Activity', types: ['link'], Icon: Gamepad2, color: '#f59e0b' },
  { key: 'quiz', label: 'Quiz', types: ['quiz'], Icon: HelpCircle, color: '#7c3aed' },
  { key: 'practice', label: 'Practice', types: ['task'], Icon: Mic, color: '#c026d3' },
];
const ITEM_TYPE_LABEL = { pdf: 'PDF', audio: 'Audio', video: 'Video', link: 'Game / Activity', quiz: 'Quiz', task: 'Practice', image: 'Image', text: 'Text' };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

function Thumb({ src, className, fallback }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <span className={className}>
      {src && !failed ? <img src={src} alt="" onError={() => setFailed(true)} /> : fallback}
    </span>
  );
}

// One content-type cell: ✓ all done, the type's icon when partly done, ○ not started, – none in the story.
function TypeCell({ column, items }) {
  const own = items.filter((item) => column.types.includes(item.type));
  if (!own.length) return <span className="sp-type sp-type--none" title={`No ${column.label} in this story`}>–</span>;
  const done = own.filter((item) => item.done).length;
  const label = `${column.label}: ${done} of ${own.length} done`;
  if (done === own.length) {
    return <span className="sp-type" title={label} aria-label={label}><CheckCircle2 size={20} color="#fff" fill="#22c55e" /></span>;
  }
  if (done) {
    return <span className="sp-type sp-type--part" title={label} aria-label={label} style={{ color: column.color, background: `${column.color}1a` }}><column.Icon size={15} /></span>;
  }
  return <span className="sp-type" title={label} aria-label={label}><Circle size={19} color="#cbd5e1" /></span>;
}

function StoryDetails({ story, index, onBack }) {
  const state = STATUS[story.status] || STATUS.not_started;
  return (
    <section className="sp-detail" aria-label={`${story.title} details`}>
      <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={onBack}><ArrowLeft size={15} />All stories</button>
      <div className="sp-detail__head">
        <Thumb src={story.thumbnail} className="sp-thumb sp-thumb--lg" fallback={<span aria-hidden="true">📖</span>} />
        <div>
          <h3>{index}. {story.title}</h3>
          <small>{story.itemsDone} of {plural(story.items.length, 'item', 'items')} done · ₹ {story.coins} coins</small>
        </div>
        <span className={`cm-pill cm-pill--${state.tone}`}>{state.label}</span>
      </div>
      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead>
            <tr><th>Sr. No.</th><th>Content</th><th>Type</th><th className="is-num">Coins (₹)</th><th>Status</th><th>Completed On</th><th className="is-num">Score</th></tr>
          </thead>
          <tbody>
            {story.items.map((item, i) => (
              <tr key={item._id}>
                <td className="is-num">{i + 1}</td>
                <td><b className="sp-title">{item.title}</b></td>
                <td>{ITEM_TYPE_LABEL[item.type] || item.type}</td>
                <td className="is-num">₹ {item.coins}</td>
                <td>{item.done ? <span className="cm-pill cm-pill--green">Done</span> : <span className="cm-pill cm-pill--gray">Not done</span>}</td>
                <td>{item.done ? fmtDate(item.completedAt) : '-'}</td>
                <td className="is-num">{item.score ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Props: `student` (the report row: _id, name, balagruhas), `category`, `courses` (the report's tabs),
 * `initialCourseId`, `onClose`, `onOpenProfile`.
 */
export default function StudentProgressPopup({ student, category, courses, initialCourseId, onClose, onOpenProfile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseId, setCourseId] = useState(initialCourseId);
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/v2/lms/admin/courses/progress-report/students/${student._id}`, { params: { category } });
      if (request === requestRef.current) setData(res.data?.data || null);
    } catch (err) {
      if (request === requestRef.current) setError(err?.response?.data?.error || "Could not load this student's progress.");
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [student._id, category]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs = data?.courses || courses;
  const course = data ? data.courses.find((c) => c._id === courseId) || data.courses[0] || null : null;
  const activeId = course?._id || courseId;
  const tabTitle = (tabs.find((c) => c._id === activeId) || tabs[0])?.title || '';
  const stories = useMemo(() => course?.stories || [], [course]);
  const pages = Math.max(1, Math.ceil(stories.length / STORIES_PER_PAGE));
  const currentPage = Math.min(page, pages);
  const pageStories = stories.slice((currentPage - 1) * STORIES_PER_PAGE, currentPage * STORIES_PER_PAGE);
  const detailIndex = stories.findIndex((s) => s._id === detailId);
  const counts = useMemo(() => {
    const by = (status) => stories.filter((s) => s.status === status).length;
    return { completed: by('completed'), inProgress: by('in_progress'), notStarted: by('not_started') };
  }, [stories]);

  const selectCourse = (id) => { setCourseId(id); setPage(1); setDetailId(null); };
  const info = data?.student || student;
  const unit = /stor/i.test(category) ? { one: 'story', many: 'stories', title: 'Stories' } : { one: 'chapter', many: 'chapters', title: 'Chapters' };
  const status = course?.assigned ? STATUS[course.status] || STATUS.not_started : null;
  const storyPct = pct(course?.storiesCompleted || 0, course?.storiesTotal || 0);

  return (
    <div className="cv-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sp-dialog" role="dialog" aria-modal="true" aria-label={`${info.name} – ${category} Progress`}>
        <header className="sp-head" aria-label="Student summary">
          <Thumb src={data?.student.photoUrl} className="sp-avatar" fallback={<span aria-hidden="true">{initials(info.name)}</span>} />
          <div className="sp-head__who">
            <h2>{info.name} – {category} Progress</h2>
            <p>
              <span>Balagruha: {info.balagruhas?.join(', ') || '-'}</span>
              <span>Category: {category}</span>
              <span>Course: {tabTitle}</span>
            </p>
          </div>
          {status && (
            <div className="sp-head__stats">
              <div className="sp-chip sp-chip--blue">
                <BookOpen size={22} />
                <div><b>{course.storiesCompleted} / {course.storiesTotal}</b><small>{unit.title} Completed</small></div>
              </div>
              <div className="sp-chip sp-chip--green">
                <CheckCircle2 size={22} color="#fff" fill="#16a34a" />
                <div><b>{course.progress}%</b><small>Overall Progress</small></div>
              </div>
              <div className="sp-chip sp-chip--plain">
                <div><small>Status</small><span className={`cm-pill cm-pill--${status.tone}`}>{status.label}</span></div>
              </div>
            </div>
          )}
          <button type="button" className="sp-close" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </header>

        <div className="sp-body">
          <div className="pr-tabs sp-tabs" role="tablist" aria-label={`${category} courses`}>
            {tabs.map((c) => (
              <button
                key={c._id}
                type="button"
                role="tab"
                aria-selected={c._id === activeId}
                className={`pr-tab${c._id === activeId ? ' is-active' : ''}`}
                onClick={() => selectCourse(c._id)}
              >
                <span className="pr-tab__icon" aria-hidden="true">{c.icon || '📚'}</span>
                <span className="pr-tab__name">{c.title}</span>
              </button>
            ))}
          </div>

          {loading && !data ? (
            <LoadingState message="Loading progress..." />
          ) : error ? (
            <div className="cv-error">
              <AlertCircle size={28} />
              <p>{error}</p>
              <div><button type="button" className="cm-btn cm-btn--purple cm-btn--sm" onClick={load}><RefreshCw size={15} />Try again</button></div>
            </div>
          ) : !course ? (
            <div className="cv-empty"><p>There are no courses in {category}.</p></div>
          ) : !course.assigned ? (
            <div className="cv-empty">
              <p>{info.name} is not assigned to {course.title}.</p>
              <small>{plural(course.storiesInCourse, unit.one, unit.many)} in this course.</small>
            </div>
          ) : detailIndex >= 0 ? (
            <StoryDetails story={stories[detailIndex]} index={detailIndex + 1} onBack={() => setDetailId(null)} />
          ) : (
            <>
              <section className="sp-progress" aria-label={`${course.title} progress`}>
                <div className="sp-progress__main">
                  <b>{course.title} Progress</b>
                  <div>
                    <span className="sp-bar" aria-hidden="true"><i style={{ width: `${storyPct}%` }} /></span>
                    <span>{course.storiesCompleted} / {course.storiesTotal} {unit.title} Completed ({storyPct}%)</span>
                  </div>
                </div>
                <div className="sp-mini sp-mini--green"><CheckCircle2 size={24} color="#fff" fill="#22c55e" /><div><b>{counts.completed}</b><small>Completed</small></div></div>
                <div className="sp-mini sp-mini--amber"><PlayCircle size={24} color="#fff" fill="#f59e0b" /><div><b>{counts.inProgress}</b><small>In Progress</small></div></div>
                <div className="sp-mini sp-mini--red"><AlertCircle size={24} color="#fff" fill="#ef4444" /><div><b>{counts.notStarted}</b><small>Not Started</small></div></div>
              </section>

              {course.storyIds && (
                <p className="pr-dlg__note">Assigned {course.storiesTotal} of {plural(course.storiesInCourse, unit.one, unit.many)} in this course.</p>
              )}

              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th className="is-num">Sr. No.</th>
                      <th>{unit.one === 'story' ? 'Story' : 'Chapter'} Title</th>
                      {TYPE_COLUMNS.map((col) => <th key={col.key} className="is-num">{col.label}</th>)}
                      <th className="is-num">Coins (₹)</th>
                      <th className="is-num">Status</th>
                      <th className="is-num">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!pageStories.length ? (
                      <tr><td colSpan={TYPE_COLUMNS.length + 5} className="cm-empty">This course has no {unit.many} with content yet.</td></tr>
                    ) : pageStories.map((story, i) => {
                      const state = STATUS[story.status] || STATUS.not_started;
                      return (
                        <tr key={story._id}>
                          <td className="is-num">{(currentPage - 1) * STORIES_PER_PAGE + i + 1}</td>
                          <td>
                            <div className="sp-story">
                              <Thumb src={story.thumbnail} className="sp-thumb" fallback={<span aria-hidden="true">📖</span>} />
                              <b className="sp-title">{story.title}</b>
                            </div>
                          </td>
                          {TYPE_COLUMNS.map((col) => <td key={col.key} className="is-num"><TypeCell column={col} items={story.items} /></td>)}
                          <td className="is-num sp-coins">₹ {story.coins}</td>
                          <td className="is-num"><span className={`cm-pill cm-pill--${state.tone} pr-status`}>{state.label}</span></td>
                          <td className="is-num">
                            <button type="button" className="sp-details" aria-label={`View details of ${story.title}`} onClick={() => setDetailId(story._id)}>
                              <Eye size={15} />View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="sp-legend">
                <span><CheckCircle2 size={15} color="#fff" fill="#22c55e" />Done</span>
                <span><span className="sp-legend__part"><FileText size={11} /></span>Partly done</span>
                <span><Circle size={14} color="#cbd5e1" />Not done yet</span>
                <span><b>–</b>Not in this {unit.one}</span>
              </p>
            </>
          )}
        </div>

        <footer className="sp-foot">
          {course?.assigned && detailIndex < 0 && pages > 1 ? (
            <nav className="sp-pager" aria-label="Story pages">
              <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}><ChevronLeft size={15} />Previous</button>
              {pageList(currentPage, pages).map((p, i) => (p === '…'
                ? <span key={`gap-${i}`} className="sp-pager__gap">…</span>
                : <button key={p} type="button" className={p === currentPage ? 'is-active' : ''} aria-current={p === currentPage ? 'page' : undefined} onClick={() => setPage(p)}>{p}</button>))}
              <button type="button" disabled={currentPage === pages} onClick={() => setPage(currentPage + 1)}>Next<ChevronRight size={15} /></button>
            </nav>
          ) : <span />}
          <div className="sp-foot__actions">
            <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={onOpenProfile}>Student Profile</button>
            <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm sp-foot__close" onClick={onClose}>Close</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
